import { Table, Typography, Stack, IconButton, Button, Chip, Tooltip } from '@mui/joy'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import GroupIcon from '@mui/icons-material/Group'

import { type TelegramGroup } from '../types'

interface GroupsTableProps {
    groups: TelegramGroup[]
    loading: boolean
    onManageGroup: (peerId: string) => void
    onDeleteGroup: (peerId: string) => void
}

export function GroupsTable({ groups, loading, onManageGroup, onDeleteGroup }: GroupsTableProps): JSX.Element {
    if (groups.length === 0) {
        return (
            <Table variant="outlined" size="sm" sx={{ tableLayout: 'fixed', '& thead th': { bgcolor: 'neutral.softBg' } }}>
                <thead>
                    <tr>
                        <th style={{ width: 200 }}>Group Name</th>
                        <th style={{ width: 140 }}>Peer ID</th>
                        <th style={{ width: 160 }}>Broker Company</th>
                        <th style={{ width: 100 }}>Status</th>
                        <th style={{ width: 140 }}>Watching Accounts</th>
                        <th style={{ width: 160 }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td colSpan={6}>
                            <Typography level="body-sm" sx={{ p: 2, textAlign: 'center' }}>
                                No groups yet. Groups will appear here when accounts join Telegram groups.
                            </Typography>
                        </td>
                    </tr>
                </tbody>
            </Table>
        )
    }

    return (
        <Table
            variant="outlined"
            size="sm"
            sx={{
                tableLayout: 'fixed',
                '& thead th': { bgcolor: 'neutral.softBg' },
            }}
        >
            <thead>
                <tr>
                    <th style={{ width: 200 }}>Group Name</th>
                    <th style={{ width: 140 }}>Peer ID</th>
                    <th style={{ width: 160 }}>Broker Company</th>
                    <th style={{ width: 100 }}>Status</th>
                    <th style={{ width: 140 }}>Watching Accounts</th>
                    <th style={{ width: 160 }}>Actions</th>
                </tr>
            </thead>
            <tbody>
                {groups.map((group) => (
                    <tr key={group.peerId}>
                        <td>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <GroupIcon fontSize="small" />
                                <Typography level="body-md" noWrap>
                                    {group.originalName || group.peerId}
                                </Typography>
                            </Stack>
                        </td>
                        <td>
                            <Chip size="sm" variant="outlined" color="neutral">
                                {group.peerId}
                            </Chip>
                        </td>
                        <td>
                            {group.brokerCompanyName ? (
                                <Chip size="sm" variant="soft" color="primary">
                                    {group.brokerCompanyName}
                                </Chip>
                            ) : (
                                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                    Not assigned
                                </Typography>
                            )}
                        </td>
                        <td>
                            <Chip size="sm" variant="soft" color={group.status === 'active' ? 'success' : 'neutral'}>
                                {group.status}
                            </Chip>
                        </td>
                        <td>
                            <Chip size="sm" variant="soft" color={group.watchingAccountsCount ? 'success' : 'neutral'}>
                                {group.watchingAccountsCount ?? 0} accounts
                            </Chip>
                        </td>
                        <td>
                            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                                <Tooltip title="Manage group">
                                    <Button size="sm" variant="soft" startDecorator={<EditIcon />} onClick={() => onManageGroup(group.peerId)}>
                                        Manage
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Delete group">
                                    <IconButton size="sm" variant="soft" color="danger" onClick={() => onDeleteGroup(group.peerId)} disabled={loading}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    )
}
